/**
 * IutePay (IuteCredit instalments). IUTE's integration guide asks for its
 * script and stylesheet on every page, set up with the store's public key
 * and the page language: `iute.configure(publicKey, lang)`. The script then
 * fills every `.iute-as-low-as` / `.iute-from` element with the monthly
 * price (one batch request per page) and opens the checkout modal from
 * `iute.checkout(payload, { onSuccess, onFailure })`.
 *
 * loadIutePay() loads it once per page view and resolves with `window.iute`.
 */
let loading = null;

export function loadIutePay({ apiBase, publicKey, lang }) {
  if (typeof window === 'undefined' || !apiBase || !publicKey) {
    return Promise.reject(new Error('IutePay is not configured'));
  }
  if (loading) return loading;

  const base = apiBase.replace(/\/$/, '');
  loading = new Promise((resolve, reject) => {
    if (!document.querySelector('link[data-iutepay]')) {
      const css = document.createElement('link');
      css.rel = 'stylesheet';
      css.href = `${base}/iutepay.css`;
      css.dataset.iutepay = '1';
      document.head.appendChild(css);
    }
    const script = document.createElement('script');
    script.src = `${base}/iutepay.js`;
    script.async = true;
    script.dataset.iutepay = '1';
    script.onload = () => {
      if (!window.iute || typeof window.iute.configure !== 'function') {
        reject(new Error('IutePay did not start'));
        return;
      }
      window.iute.configure(publicKey, lang || 'sq');
      resolve(window.iute);
    };
    script.onerror = () => {
      loading = null; // let a later page try again
      reject(new Error(`IutePay could not be loaded from ${base}`));
    };
    document.head.appendChild(script);
  });
  return loading;
}

/** The SKU IUTE sees for a product; matches IutePayService::skuFor on the server. */
export const iuteSku = (product) => (product?.sku && String(product.sku).trim()) || `P-${product?.id ?? 'NA'}`;

/**
 * IUTE's cheapest monthly payment for an amount, or null when it doesn't
 * finance it (below its minimum, around 5 000 L in Albania) or can't be
 * asked. Same public calculation the price widgets use.
 */
export async function iuteMonthly({ apiBase, publicKey, amount }) {
  const value = Math.round(Number(amount));
  if (!apiBase || !publicKey || !Number.isFinite(value) || value <= 0) return null;
  try {
    const res = await fetch(`${apiBase.replace(/\/$/, '')}/api/v1/eshop/client/eshop-product/-/calculation?monthly=true`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-iute-api-key': publicKey },
      body: JSON.stringify([{ id: 'cart', sku: 'cart', amount: String(value) }]),
    });
    if (!res.ok) return null;
    const [offer] = await res.json();
    return offer?.monthlyRepayment || null;
  } catch {
    return null;
  }
}
